import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/forgot-password", { email });
      if (res.data?.reset_link) setDevLink(res.data.reset_link);
      setSent(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="forgot-page" className="min-h-[80vh] flex items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white border border-slate-200 rounded-md p-10">
          <Link to="/login" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-4">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75}/> Back to sign in
          </Link>
          <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Reset your password.</h1>
          <p className="mt-2 text-slate-600 text-sm">Enter your email — we'll send a reset link.</p>
          {sent ? (
            <div className="mt-6 rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900" data-testid="forgot-sent">
              If an account exists for <b>{email}</b>, a password reset link is on its way.
              {devLink && (
                <div className="mt-3 font-mono text-[11px] break-all border-t border-blue-200 pt-3">
                  Dev link:<br/>
                  <a href={devLink} className="text-blue-700 underline">{devLink}</a>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-3">
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.75}/>
                <Input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@lab.com" className="h-11 pl-9 rounded-md border-slate-300"/>
              </div>
              <Button data-testid="forgot-submit" type="submit" disabled={busy} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-md">
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
